import type { Building } from "building-types/building/buildingTypes";
import type { Casework } from "building-types/casework/caseworkTypes";
import type {
  ControllerPlacementCandidateInfo,
  ControllerPlacementCandidateTable,
} from "building-types/electrical/candidate";
import type {
  CSNearestExplicitWall,
  ControllerSelectionConfig,
  PlacementPoint,
  QueryPoint,
} from "building-types/electrical/controllerSelectionTypes";
import { ControllerSelectionType } from "building-types/electrical/electricalTypesEnum";
import type { ReferenceGeom } from "building-types/entities/referenceGeom";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import type { MergedPropLibrary } from "doc/propTools";
import type { RoomIdToRoomSetIndexLookup } from "doc/rooms/roomSetTypes";
import { polyLine2EdgeEval, polyLine2EdgeLength, polyLine2EdgeNormal } from "geom/polyLine2";
import type { Vec2 } from "geom/vec2";
import { vec2Scale } from "geom/vec2";
import type { CandidateFilteringCriteria, ExplicitControllerSelectionFunc } from "../electricalLayoutTypes";
import type { RoomSetInfoTable } from "../electricalRoomSetInfo";
import { reverseParam } from "../reverseParam";
import { resolveControllerPosition } from "../stages/resolveRoomPosition";
import {
  addExplicitCandidate,
  addExplicitPointCandidate,
  byControllerTag,
  doorByRoomIdPlacement,
  doorByRoomType,
  doorByRoomTypeOutside,
  nearestCandidate,
  nearestCandidateOutsideRoomSet,
  nearestDoorOutsideRoomPlacement,
  primaryEntranceOutsideRoomPlacement,
  primaryEntrancePlacement,
} from "./controllerSelectionFuncs";
import { candidateFilteringCriteriaNew, type ControllerSelectionFunc } from "./controllerSelectionShared";
import type { PlacementPointAndNormal, QueryPointAndNormal } from "./controllersSelectionTypes";

interface ControllerSelectionFuncs {
  controllerSelectionFunc: ControllerSelectionFunc;
  explicitControllerSelectionFunc: ExplicitControllerSelectionFunc;
  updateControllerLookupsForNewIdsAndTags: () => void;
}

export function controllerSelectionFuncNew(
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
  candidateTable: ControllerPlacementCandidateTable,
  roomSetInfoTable: RoomSetInfoTable,
  roomTable: Table<Room>,
  wallSystems: Table<WallSystem>,
  docRefGeom: Table<ReferenceGeom>,
  props: Table<Prop>,
  casework: Table<Casework>,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  mirror: boolean,
  roomOutlines: Table<RoomOutlines>,
  wallMasses: Table<WallMassSystem>,
  roomIndex: RoomIndex,
  wallLayerExtentsCache: WallLayerExtentsCache,
): ControllerSelectionFuncs {
  const candidateTagLookup: { [tag: string]: ControllerPlacementCandidateInfo[] } = {};

  updateLookups(candidateTable, candidateTagLookup);

  return {
    controllerSelectionFunc: async (
      controllerSelectionConfig: ControllerSelectionConfig[],
      primaryRoomId: string,
      approximateLoadPosition?: Vec2,
      candidateFilteringCriteria?: CandidateFilteringCriteria,
    ) => {
      for (const config of controllerSelectionConfig) {
        const controller = await selectController(
          config,
          roomTable,
          roomIdToRoomSetIndexLookup,
          roomSetInfoTable,
          candidateTable,
          candidateTagLookup,
          primaryRoomId,
          wallSystems,
          docRefGeom,
          props,
          casework,
          propCatalog,
          mergedPropLibrary,
          mirror,
          roomOutlines,
          wallMasses,
          roomIndex,
          wallLayerExtentsCache,
          approximateLoadPosition,
          candidateFilteringCriteria,
        );
        if (controller) {
          return controller;
        }
      }

      // console.warn("none of the prioritized controller selections were chosen");
      return null;
    },

    explicitControllerSelectionFunc: (controllerSelectionConfig: ControllerSelectionConfig[]) => {
      for (const opt of controllerSelectionConfig) {
        if (opt.type !== ControllerSelectionType.NEAREST_EXPLICIT_WALL) {
          continue;
        }
        const controller = explicitSelectController(
          opt,
          roomTable,
          roomIdToRoomSetIndexLookup,
          candidateTable,
          wallSystems,
        );
        if (controller) {
          return controller;
        }
      }

      // console.warn("none of the prioritized controller selections were chosen");
      return null;
    },
    updateControllerLookupsForNewIdsAndTags: () => {
      updateLookups(candidateTable, candidateTagLookup);
    },
  };
}

function explicitSelectController(
  opt: CSNearestExplicitWall,
  roomTable: Table<Room>,
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
  candidateTable: ControllerPlacementCandidateTable,
  wallSystems: Table<WallSystem>,
): ControllerPlacementCandidateInfo | null {
  const { explicitQueryPoint: qp, tag, snapCandidateTypeConstraints, snapToCandidateMaxSearchDistance } = opt;
  let queryPointAndNormal: QueryPointAndNormal | null = null;
  switch (qp.type) {
    case "LEGACY": {
      queryPointAndNormal = getOutlineBaselineQueryPointAndNormal(qp, roomTable, wallSystems);
      break;
    }
    case "CONTROLLER_POSITION": {
      break;
    }
    case "LEGACY_PLACEMENT_POINT": {
      queryPointAndNormal = getOutlineBaselinePlacementPointAndNormal(qp, roomTable, wallSystems);
      break;
    }
  }

  if (!queryPointAndNormal) {
    return null;
  }

  const { roomId } = qp;
  const roomSetIndex = roomIdToRoomSetIndexLookup[roomId];

  const candidate = addExplicitCandidate(
    queryPointAndNormal,
    candidateTable,
    snapCandidateTypeConstraints,
    snapToCandidateMaxSearchDistance,
    roomSetIndex,
    roomId,
    tag,
  );

  if (candidate) {
    tagCandidate(candidate, tag);
    return candidate;
  }

  return null;
}

async function selectController(
  opt: ControllerSelectionConfig,
  roomTable: Table<Room>,
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
  roomSetInfoTable: RoomSetInfoTable,
  candidateTable: ControllerPlacementCandidateTable,
  candidateTagLookup: { [tag: string]: ControllerPlacementCandidateInfo[] },
  primaryRoomId: string,
  wallSystems: Table<WallSystem>,
  docRefGeom: Table<ReferenceGeom>,
  props: Table<Prop>,
  casework: Table<Casework>,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  mirror: boolean,
  roomOutlines: Table<RoomOutlines>,
  wallMasses: Table<WallMassSystem>,
  roomIndex: RoomIndex,
  wallLayerExtentsCache: WallLayerExtentsCache,
  approximateLoadPosition?: Vec2,
  candidateFilteringCriteria?: CandidateFilteringCriteria,
): Promise<ControllerPlacementCandidateInfo | null> {
  const primaryRoomSetIndex = roomIdToRoomSetIndexLookup[primaryRoomId];

  if (opt.type === ControllerSelectionType.NONE) {
    return null;
  }

  if (!candidateFilteringCriteria) {
    candidateFilteringCriteria = candidateFilteringCriteriaNew();
  }

  switch (opt.type) {
    case ControllerSelectionType.PRIMARY_ENTRANCE: {
      const roomSetInfo = roomSetInfoTable[primaryRoomSetIndex];
      const { doorScores } = roomSetInfo;
      const { queryPoint: qp, tag } = opt;
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;
      const candidate = primaryEntrancePlacement(
        doorScores,
        candidateTable,
        primaryRoomSetIndex,
        candidateFilteringCriteria,
        queryPoint,
      );
      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.PRIMARY_ENTRANCE_OUTSIDE_ROOMSET: {
      const { queryPoint: qp, tag } = opt;
      const roomSetInfo = roomSetInfoTable[primaryRoomSetIndex];
      if (!roomSetInfo) {
        return null;
      }
      const { doorScores } = roomSetInfo;
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;
      const candidate = primaryEntranceOutsideRoomPlacement(
        doorScores,
        candidateTable,
        candidateFilteringCriteria,
        queryPoint,
      );
      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.NEAREST_CANDIDATE: {
      const { queryPoint: qp, candidateTypeConstraints, tag } = opt;
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;
      const candidate = nearestCandidate(
        queryPoint,
        primaryRoomSetIndex,
        candidateTable,
        candidateTypeConstraints,
        candidateFilteringCriteria,
      );
      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.NEAREST_CANDIDATE_OUTSIDE_ROOMSET: {
      const roomSetInfo = roomSetInfoTable[primaryRoomSetIndex];
      if (!roomSetInfo) {
        return null;
      }
      const { levelId } = roomSetInfo;
      const { queryPoint: qp, candidateTypeConstraints, tag } = opt;
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;
      const candidate = nearestCandidateOutsideRoomSet(
        queryPoint,
        primaryRoomSetIndex,
        candidateTable,
        candidateTypeConstraints,
        levelId,
        candidateFilteringCriteria,
      );
      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.NEAREST_DOOR_OUTSIDE_ROOMSET: {
      const roomSetInfo = roomSetInfoTable[primaryRoomSetIndex];
      if (!roomSetInfo) {
        return null;
      }
      const { doorScores } = roomSetInfo;
      const { queryPoint: qp, doorCandidateTypeConstraints, tag } = opt;
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;
      const candidate = nearestDoorOutsideRoomPlacement(
        queryPoint,
        doorScores,
        candidateTable,
        candidateFilteringCriteria,
        doorCandidateTypeConstraints,
      );
      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.BY_CONTROLLER_TAG: {
      const { tagToLookup, queryPoint: qp, candidateTypeConstraints, tag } = opt;
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;

      const candidate = byControllerTag(
        queryPoint,
        candidateTagLookup,
        tagToLookup,
        candidateTypeConstraints,
        candidateFilteringCriteria,
      );

      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.DOOR_BY_ROOM_TYPE: {
      const { queryPoint: qp, roomType, spaceType, tag, doorCandidateTypeConstraints } = opt;
      const { doorScores, spaceTypesTable } = roomSetInfoTable[primaryRoomSetIndex];
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;
      const candidate = doorByRoomType(
        queryPoint,
        doorScores,
        candidateTable,
        doorCandidateTypeConstraints,
        roomTable,
        spaceType,
        roomType,
        spaceTypesTable,
        primaryRoomSetIndex,
        candidateFilteringCriteria,
      );
      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.DOOR_BY_ROOM_TYPE_OUTSIDE_ROOMSET: {
      const { queryPoint: qp, roomType, spaceType, tag, doorCandidateTypeConstraints } = opt;
      const { doorScores, spaceTypesTable } = roomSetInfoTable[primaryRoomSetIndex];
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;
      const candidate = doorByRoomTypeOutside(
        queryPoint,
        doorScores,
        candidateTable,
        doorCandidateTypeConstraints,
        roomTable,
        spaceType,
        roomType,
        spaceTypesTable,
        candidateFilteringCriteria,
      );
      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.DOOR_BY_ROOM_IDS: {
      const { queryPoint: qp, tag, roomId, oppositeRoomId, doorCandidateTypeConstraints } = opt;
      const roomSetIndex = roomId !== undefined && roomIdToRoomSetIndexLookup[roomId];
      if (!roomSetIndex) {
        return null;
      }
      const queryPoint = qp ? getQueryPoint(qp, roomTable) : approximateLoadPosition;
      const candidate = doorByRoomIdPlacement(
        queryPoint,
        roomSetIndex,
        candidateTable,
        oppositeRoomId,
        doorCandidateTypeConstraints,
        candidateFilteringCriteria,
      );
      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.NEAREST_EXPLICIT_WALL: {
      const { explicitQueryPoint: qp, tag, snapCandidateTypeConstraints, snapToCandidateMaxSearchDistance } = opt;
      let queryPointAndNormal: QueryPointAndNormal | undefined | null;
      switch (qp.type) {
        case "LEGACY": {
          queryPointAndNormal = getOutlineBaselineQueryPointAndNormal(qp, roomTable, wallSystems);
          break;
        }
        case "CONTROLLER_POSITION": {
          queryPointAndNormal = await resolveControllerPosition(
            qp,
            roomTable,
            docRefGeom,
            props,
            casework,
            propCatalog,
            mergedPropLibrary,
            mirror,
            roomOutlines,
            wallMasses,
            roomIndex,
            wallLayerExtentsCache,
          );

          break;
        }
        case "LEGACY_PLACEMENT_POINT": {
          queryPointAndNormal = getOutlineBaselinePlacementPointAndNormal(qp, roomTable, wallSystems);
          break;
        }
      }

      if (!queryPointAndNormal) {
        return null;
      }
      const { roomId } = qp;
      const roomSetIndex = roomIdToRoomSetIndexLookup[roomId];
      const candidate = addExplicitCandidate(
        queryPointAndNormal,
        candidateTable,
        snapCandidateTypeConstraints,
        snapToCandidateMaxSearchDistance,
        roomSetIndex,
        roomId,
        tag,
      );

      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
      break;
    }
    case ControllerSelectionType.EXPLICIT_POINT: {
      const { explicitQueryPoint: placementPoint, tag } = opt;
      let queryPointAndNormal: QueryPointAndNormal | undefined | null;
      switch (placementPoint.type) {
        case "LEGACY": {
          queryPointAndNormal = getOutlineBaselineQueryPointAndNormal(placementPoint, roomTable, wallSystems);
          break;
        }
        case "CONTROLLER_POSITION": {
          queryPointAndNormal = await resolveControllerPosition(
            placementPoint,
            roomTable,
            docRefGeom,
            props,
            casework,
            propCatalog,
            mergedPropLibrary,
            mirror,
            roomOutlines,
            wallMasses,
            roomIndex,
            wallLayerExtentsCache,
          );
          break;
        }
        case "LEGACY_PLACEMENT_POINT": {
          queryPointAndNormal = getOutlineBaselinePlacementPointAndNormal(placementPoint, roomTable, wallSystems);
          break;
        }
      }

      if (!queryPointAndNormal) {
        return null;
      }
      const { roomId } = placementPoint;
      const roomSetIndex = roomIdToRoomSetIndexLookup[roomId];
      const candidate = addExplicitPointCandidate(queryPointAndNormal, candidateTable, roomSetIndex, roomId, tag);

      if (candidate) {
        tagCandidate(candidate, tag);
        return candidate;
      }
    }
  }

  return null;
}

function tagCandidate(candidate: ControllerPlacementCandidateInfo, tag: string | undefined) {
  if (tag !== undefined && tag !== "") {
    candidate.candidate.tags[tag] = true;
  }
}

function updateLookups(
  candidateTable: ControllerPlacementCandidateTable,
  candidateTagLookup: { [tag: string]: ControllerPlacementCandidateInfo[] },
) {
  for (const roomSetKey in candidateTable) {
    const roomSetCandidates = candidateTable[roomSetKey];
    for (const candKey of Object.keys(roomSetCandidates)) {
      const cand = roomSetCandidates[candKey];
      for (const tag in cand.candidate.tags) {
        if (tag === undefined || tag === "") {
          continue;
        }
        const candListByTag = candidateTagLookup[tag] || [];

        let alreadyInList = false;
        for (const c of candListByTag) {
          if (c.candidate.id === cand.candidate.id) {
            alreadyInList = true;
          }
        }

        if (!alreadyInList) {
          candListByTag.push(cand);
          candidateTagLookup[tag] = candListByTag;
        }
      }
    }
  }
}

function getQueryPoint(qp: QueryPoint, roomTable: Table<Room>) {
  if (qp) {
    const { roomId, edgeId, param, paramNormalized, reverse } = qp;
    const room = roomTable[roomId];

    if (!room) {
      return null;
    }
    const { outline, edgeIds } = room;
    const edgeIndex = edgeIds.indexOf(edgeId);

    if (edgeIndex === undefined) {
      return null;
    }
    const edgeLength = polyLine2EdgeLength(outline, edgeIndex);
    const [x, y] = reverse
      ? polyLine2EdgeEval(outline, edgeIndex, reverseParam(param, paramNormalized, edgeLength), paramNormalized)
      : polyLine2EdgeEval(outline, edgeIndex, param, paramNormalized);
    return [x, y];
  }

  return null;
}

function getOutlineBaselineQueryPointAndNormal(
  qp: QueryPoint,
  roomTable: Table<Room>,
  wallSystems: Table<WallSystem>,
): QueryPointAndNormal | null {
  if (!qp) {
    return null;
  }

  const { roomId, edgeId, param, paramNormalized, reverse } = qp;
  const room = roomTable[roomId];

  if (!room) {
    // console.warn(`room id [ ${roomId} ] not found in room table`);
    return null;
  }
  const {
    level: { id: levelId },
    edgeIds,
  } = room;
  const edgeIndex = edgeIds.indexOf(edgeId);

  if (edgeIndex === undefined) {
    // console.warn(`edge index of ${edgeId} not found in indices ${edgeIds}`);
    return null;
  }
  const wallSystem = wallSystems[levelId];
  const wallIndex = wallSystem.wallIndex;
  const strip = wallIndex.wallStripFromRoomEdgeIndex(room, edgeIndex);
  if (!strip) {
    return null;
  }
  const edge = strip.baseline(true);
  const edgeLength = polyLine2EdgeLength(edge, 0);
  const [nX, nY] = polyLine2EdgeNormal(edge, 0, true);
  const [x, y] = reverse
    ? polyLine2EdgeEval(edge, 0, reverseParam(param, paramNormalized, edgeLength), paramNormalized)
    : polyLine2EdgeEval(edge, 0, param, paramNormalized);

  return { point: [x, y], normal: [nX, nY], levelId };
}

function getOutlineBaselinePlacementPointAndNormal(
  pp: PlacementPoint,
  roomTable: Table<Room>,
  wallSystems: Building["walls"],
): PlacementPointAndNormal | null {
  if (!pp) {
    return null;
  }

  const { roomId, edgeId, param, paramNormalized, reverse, offset } = pp;
  const room = roomTable[roomId];

  if (!room) {
    // console.warn(`room id [ ${roomId} ] not found in room table`);
    return null;
  }
  const {
    level: { id: levelId },
    edgeIds,
  } = room;
  const edgeIndex = edgeIds.indexOf(edgeId);

  if (edgeIndex === undefined) {
    // console.warn(`edge index of ${edgeId} not found in indices ${edgeIds}`);
    return null;
  }
  const wallSystem = wallSystems[levelId];
  const wallIndex = wallSystem.wallIndex;
  const strip = wallIndex.wallStripFromRoomEdgeIndex(room, edgeIndex);
  if (!strip) {
    return null;
  }
  const edge = strip.baseline(true);
  const edgeLength = polyLine2EdgeLength(edge, 0);
  const [nX, nY] = polyLine2EdgeNormal(edge, 0, true);
  const [onX, onY] = vec2Scale(nX, nY, offset);
  const [x, y] = reverse
    ? polyLine2EdgeEval(edge, 0, reverseParam(param, paramNormalized, edgeLength), paramNormalized)
    : polyLine2EdgeEval(edge, 0, param, paramNormalized);

  return { point: [x + onX, y + onY], normal: [nX, nY], levelId };
}
